import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api, ApiError, requestPage, type Page } from '@/lib/api';
import { afterClaimChange, qk } from '@/lib/query-keys';
import type { Claim, ChatStatus, QueueFilter, ReviewQueueItem } from '@/types/api';

/* Types are generated from the contract; hooks are hand-written. Generated
   hooks bury the cross-entity invalidation, which is the actually hard part
   here: a claim changes the chat, every filter of the queue and the project
   list's row state, and a generator has no way to know that. */

export interface ClaimResult {
  chat_id: number;
  claim?: Claim;
  status?: ChatStatus;
}

export function useQueue(filter: QueueFilter, page: number): UseQueryResult<Page<ReviewQueueItem>, ApiError> {
  return useQuery({
    queryKey: qk.queue(filter, page),
    queryFn: () => requestPage<ReviewQueueItem>('/expert/queue', { query: { filter, page, per_page: 25 } }),
    // A chat entering review is a project that just froze and a client who can
    // no longer prompt. Nobody should have to reload to find that out, and the
    // queue has no stream of its own.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
  });
}

/**
 * The unclaimed count, for the nav badge.
 *
 * Deliberately a separate one-row query rather than a read off the list: the
 * badge has to be right on every screen, including the ones that never render
 * the queue, and `meta.total_items` is exact where a page of rows is not.
 */
export function useUnclaimedCount(): number | null {
  const query = useQuery({
    queryKey: [...qk.queueAll, 'count'] as const,
    queryFn: () => requestPage<ReviewQueueItem>('/expert/queue', { query: { filter: 'unclaimed', page: 1, per_page: 1 } }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  return query.data?.meta?.total_items ?? null;
}

/**
 * Check a chat out.
 *
 * The claim is a compare-and-set on `assigned_expert_user_id` taken with the
 * project row locked; it moves no status and touches no `status_version`, so it
 * cannot lose a race to a concurrent transition and a transition cannot
 * invalidate it. Two consequences the UI depends on:
 *
 *   * the loser of a race gets a 409 whose body NAMES the holder, which is why
 *     `ApiError.claim` exists and why the queue can say who rather than "try
 *     again";
 *   * re-claiming a chat you already hold is a 200 that extends the lease. So
 *     this same mutation is the lease refresh, and calling it on reconnect is
 *     correct rather than an error.
 */
export function useClaim(chatId: number) {
  const qc = useQueryClient();
  return useMutation<ClaimResult, ApiError, void>({
    mutationFn: () => api.post<ClaimResult>(`/expert/queue/${chatId}/claim`),
    onSettled: () => afterClaimChange(qc, chatId),
  });
}

/**
 * Release a claim back to the queue.
 *
 * Idempotent — releasing a chat you do not hold is a 200, because the
 * alternative is an error dialog on a browser back button.
 *
 * Releasing does NOT unfreeze the project and does not change the status: the
 * chat stays in `pending review`, which is where an unclaimed chat belongs.
 * Handing the thread back to the client is Request changes, which is a
 * different decision and the only one that reaches `changes requested`.
 */
export function useRelease(chatId: number) {
  const qc = useQueryClient();
  return useMutation<ClaimResult, ApiError, void>({
    mutationFn: () => api.del<ClaimResult>(`/expert/queue/${chatId}/claim`),
    onSettled: () => afterClaimChange(qc, chatId),
  });
}
